
import { PieceStatus, Role, TabType, SampleRequest } from './types';
import { DS } from './theme/DesignSystem';

// 1. DOMAIN: Role Capabilities
export const getTabsForRole = (role: Role): TabType[] => {
  switch (role) {
    case 'cutting_incharge': return ['cutting'];
    case 'supervisor': return ['sewing'];
    case 'inline_qc': return ['sewing']; // Inline QC sits in Sewing line conceptually
    case 'qc': return ['qc'];
    case 'admin': return ['cutting', 'sewing', 'qc'];
    default: return [];
  }
};

// 2. DOMAIN: Visual Status
export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'File Handover': return DS.status.neutral;
    case 'Cutting': case 'Cutting Started': return DS.status.brand;
    case 'Cutting Completed': return DS.status.purple;
    case 'Sewing': case 'Sewing Started': return DS.status.brand;
    case 'Sewing Completed': return DS.status.purple;
    case 'QC': case 'QC Ongoing': return DS.status.warning;
    case 'Inline Pass': return DS.status.success;
    case 'Final Pass': return DS.status.success;
    case 'Inline Rework': return DS.status.warning;
    case 'Final Rework': case 'Rework Required': return DS.status.error;
    case 'Buyer Rework': return DS.status.error;  // New: Buyer-initiated rework
    case 'Sample Completed': return DS.status.success;
    case 'Scrapped': case 'Lost': return DS.status.neutral;
    // Planning statuses
    case 'Fresh Planned': return DS.status.success;
    case 'Rework Planned': return DS.status.error;
    case 'Fresh In Progress': return DS.status.brand;
    case 'Rework In Progress': return DS.status.warning;
    default: return DS.status.neutral;
  }
};

// Helper: Derived Status for Sample Request
// Uses sample_production.status as the primary source of truth for floor operations
export const getDerivedStatus = (sample: SampleRequest): string => {
  // Priority 1: Use sample_production.status (floor execution status)
  const p = sample.sample_production;
  if (p?.status) {
    const status = p.status;
    
    // Terminal / completed states
    if (['Sample Completed'].includes(status)) return 'Completed';
    
    // Return production status directly for active floor operations
    if (['Cutting Started', 'Cutting Completed', 'Sewing Started', 'Sewing Completed', 
         'Inline QC', 'Final QC', 'Rework Required'].includes(status)) {
      return status;
    }
    
    // Map generic statuses
    if (status === 'File Handover' || status === 'File Received') return 'File Handover';
    if (status === 'Planned') return 'Planned';
    
    return status;
  }

  // Priority 3: Fall back to request status
  return sample.status || 'File Handover';
};

export const isCuttingDone = (sample: SampleRequest): boolean => {
  const status = sample.sample_production?.status || '';
  // Cutting is done if production has moved past Cutting Completed
  const cuttingDoneStatuses = [
    'Cutting Completed', 'Sewing Started', 'Sewing Completed',
    'QC Ongoing', 'Rework Required',
    'Sample Completed', 'Completed',
  ];
  return cuttingDoneStatuses.includes(status);
};

// 3. DOMAIN: List Filtering
export const isPieceInTab = (pieceStatus: PieceStatus, tab: TabType): boolean => {
  switch (tab) {
    case 'cutting':
      return ['File Handover', 'Cutting Started', 'Cutting Completed'].includes(pieceStatus);
    case 'sewing':
      return ['Cutting Completed', 'Sewing Started', 'Sewing Completed', 
              'Buyer Rework', 'Inline Pass', 'Inline Rework', 'Final Rework'].includes(pieceStatus);
    case 'qc':
      return ['Inline Pass', 'Final Pass', 'Final Rework', 'Sample Completed'].includes(pieceStatus);
    default:
      return false;
  }
};

export interface ActionDef {
  label: string;
  action: 'assign_tailor' | 'mark_sewing_done' | 'internal_qc' | 'final_qc';
  color: string;
}

// 4. DOMAIN: Action Availability (The Core Logic)
export const getAvailableAction = (status: PieceStatus, role: Role): ActionDef | null => {

  // STATION 2: Sewing Supervisor
  if (role === 'supervisor') {
    const preSewingStatuses: string[] = ['Cutting Completed', 'Inline Rework', 'Final Rework', 'Buyer Rework'];
    if (preSewingStatuses.includes(status)) {
      return { label: 'Assign Tailor', action: 'assign_tailor', color: DS.button.primary };
    }
    if (status === 'Sewing Started') {
      return { label: 'Mark Done', action: 'mark_sewing_done', color: DS.button.primary };
    }
  }

  // STATION 3: Inline QC
  if (role === 'inline_qc') {
    // Can inspect pieces ready for QC or needing rework (including after Final QC fail)
    if (status === 'Sewing Completed' || status === 'Inline Rework' || status === 'Final Rework') {
      return { label: 'Start Check', action: 'internal_qc', color: DS.button.primary };
    }
  }

  // STATION 4: Final QC (formerly Endline QC)
  if (role === 'qc') {
    // Can inspect pieces that passed inline QC (Inline Pass status)
    // After inspection, transitions to: Final Pass (if Pass) or Final Rework (if Fail)
    if (status === 'Inline Pass') {
      return { label: 'Final QC Check', action: 'final_qc', color: DS.button.primary };
    }
  }

  return null;
};

export const formatDate = (dateString: string | null) => {
  if (!dateString) return '--';
  const date = new Date(dateString);
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  if (isToday) return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};
